// Utility for managing JWT tokens with automatic refresh
// This can be used by services that need valid tokens

import { environment } from '../config/environment';

interface User {
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  exp?: number;
  [key: string]: any;
}

const decodeJwt = (token: string): User => {
  const payload = token.split(".")[1];
  return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
};

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = decodeJwt(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp ? decoded.exp < currentTime : true;
  } catch {
    return true;
  }
};

const isTokenExpiringSoon = (token: string, bufferMinutes: number = 5): boolean => {
  try {
    const decoded = decodeJwt(token);
    const currentTime = Date.now() / 1000;
    const bufferSeconds = bufferMinutes * 60;
    return decoded.exp ? decoded.exp < (currentTime + bufferSeconds) : true;
  } catch {
    return true;
  }
};

const refreshToken = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${environment.api.bff}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const data = await response.json();
    const newIdToken = data.id_token;
    const newAccessToken = data.access_token;

    if (!newIdToken || !newAccessToken) {
      throw new Error('Missing tokens in refresh response');
    }

    // Update stored tokens
    localStorage.setItem('idToken', newIdToken);
    localStorage.setItem('accessToken', newAccessToken);

    const decodedUser = data.user;
    localStorage.setItem('user', JSON.stringify(decodedUser));

    return newIdToken;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Clear invalid tokens
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    return null;
  }
};

export const getValidToken = async (): Promise<string | null> => {
  const storedToken = localStorage.getItem('idToken');

  if (!storedToken) {
    return null;
  }

  // Check if token is expired or expiring soon
  if (isTokenExpired(storedToken)) {
    console.log('Token is expired, attempting refresh...');
    return await refreshToken();
  }

  if (isTokenExpiringSoon(storedToken)) {
    console.log('Token is expiring soon, attempting refresh...');
    const newToken = await refreshToken();
    return newToken || storedToken; // Fallback to current token if refresh fails
  }

  return storedToken;
};

export const forceTokenRefresh = async (): Promise<string | null> => {
  return await refreshToken();
};